<template>
  <NavBar />
  <BackgroundImage
    :imageUrl="require('@/assets/login/bg.png')"
    :customStyle="{
      minHeight: '100vh',
    }"
  >
    <div class="container">
      <a-flex gap="middle" justify="center" vertical style="margin-left:-8px;">
        <a-row :gutter="[0, 24]" justify="center">
          <!-- 搜索栏 -->
          <a-col :md="24" :sm="24" :xs="24">
            <a-card class="search-card">
              <div class="search-bar">
                <a-input
                  v-model:value="searchKeyword"
                  placeholder="搜索名医/学派/专长… 例如：肝郁、妇科、温病学派"
                  @pressEnter="handleSearch"
                >
                  <template #prefix>
                    <SearchOutlined />
                  </template>
                </a-input>
                <a-button type="primary" @click="handleSearch">
                  搜索
                </a-button>
              </div>
              <div class="filter-chips">
                <a-tag
                  v-for="tag in filterTags"
                  :key="tag"
                  :color="selectedTags.includes(tag) ? 'blue' : 'default'"
                  class="filter-chip"
                  @click="toggleTag(tag)"
                >
                  {{ tag }}
                </a-tag>
              </div>
            </a-card>
          </a-col>

          <!-- 名医档案 -->
          <a-col :md="24" :sm="24" :xs="24">
            <a-card class="profile-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                名医档案（示例：朱良春学派）
              </h2>
              <a-row :gutter="[24, 24]">
                <a-col :md="14" :sm="24" :xs="24">
                  <div class="doctor-profile">
                    <div class="profile-header">
                      <div class="doctor-avatar">医</div>
                      <div class="doctor-info">
                        <h3 class="doctor-name">朱良春（传承团队）</h3>
                        <p class="doctor-specialty">专长：肝郁脾虚、湿热过盛、妇科调经、皮科湿疹</p>
                        <div class="doctor-tags">
                          <a-tag color="blue">疏肝解郁</a-tag>
                          <a-tag color="blue">健脾化湿</a-tag>
                          <a-tag color="blue">清热解毒</a-tag>
                        </div>
                      </div>
                    </div>
                    <p class="academic-summary">
                      <strong>学术思想摘要：</strong>强调"肝脾同治、气机调和"，以疏肝理气为纲，佐以健脾化湿与清热解毒，注重"君臣佐使"配伍与加减灵活。
                    </p>
                    <div class="quick-pills">
                      <span class="pill">📘 学派纲要</span>
                      <span class="pill">🧪 代表方剂</span>
                      <span class="pill">🏷️ 适应专病</span>
                    </div>
                  </div>
                </a-col>
                <a-col :md="10" :sm="24" :xs="24">
                  <div class="contact-card">
                    <h4 class="contact-title">对接与合作</h4>
                    <div class="contact-content">
                      <div class="qr-code">二维码名片</div>
                      <div class="contact-info">
                        <p class="contact-desc">可预约团队坐诊与线上教学，支持院内制剂合作与数字化整理。</p>
                        <div class="contact-actions">
                          <a-button type="primary" @click="notify('已发送预约意向（示例）')">
                            预约意向
                          </a-button>
                          <a-button @click="notify('进入合作表单（示例）')">
                            合作表单
                          </a-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 师承谱系 -->
          <a-col :md="24" :sm="24" :xs="24">
            <a-card class="lineage-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                师承谱系（可视化时间线）
              </h2>
              <div class="timeline">
                <div
                  v-for="(item, index) in lineageData"
                  :key="index"
                  class="timeline-item"
                >
                  <div class="timeline-year">{{ item.year }}</div>
                  <div class="timeline-content">{{ item.text }}</div>
                </div>
              </div>
            </a-card>
          </a-col>

          <!-- 经典验方 -->
          <a-col :md="24" :sm="24" :xs="24">
            <a-card class="formulas-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                经典验方（结构化知识）
              </h2>
              <a-row :gutter="[16, 16]">
                <a-col
                  v-for="formula in formulasData"
                  :key="formula.name"
                  :md="8"
                  :sm="12"
                  :xs="24"
                >
                  <div class="formula-card">
                    <h4 class="formula-name">{{ formula.name }}</h4>
                    <p class="formula-detail"><strong>主治：</strong>{{ formula.usage }}</p>
                    <p class="formula-detail"><strong>功效：</strong>{{ formula.actions }}</p>
                    <p class="formula-detail"><strong>组成：</strong>{{ formula.herbs }}</p>
                    <div class="formula-tags">
                      <a-tag color="green">可推送到专药</a-tag>
                      <a-tag color="cyan">AI加减</a-tag>
                    </div>
                    <div class="formula-actions">
                      <a-button type="primary" @click="notify(`已推送到专药：${formula.name}`)">
                        推送到专药
                      </a-button>
                      <a-button @click="notify('加入病例方案（示例）')">
                        加入案例
                      </a-button>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 授课与讲座 -->
          <a-col :md="24" :sm="24" :xs="24">
            <a-card class="videos-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                授课与讲座（视频/课件）
              </h2>
              <a-row :gutter="[16, 16]">
                <a-col
                  v-for="video in videosData"
                  :key="video.title"
                  :md="8"
                  :sm="12"
                  :xs="24"
                >
                  <div class="video-card">
                    <div class="video-icon">🎬</div>
                    <h4 class="video-title">{{ video.title }}</h4>
                    <p class="video-meta">{{ video.type }} · 时长 {{ video.length }}</p>
                    <div class="video-actions">
                      <a-button type="primary" @click="notify('开始播放（占位）')">
                        播放
                      </a-button>
                      <a-button @click="notify('下载课件（占位）')">
                        课件
                      </a-button>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 门诊排班 -->
          <a-col :md="24" :sm="24" :xs="24">
            <a-card class="clinic-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                门诊排班与联系方式
              </h2>
              <a-row :gutter="[24, 24]">
                <a-col :md="14" :sm="24" :xs="24">
                  <a-table
                    :columns="scheduleColumns"
                    :data-source="scheduleData"
                    :pagination="false"
                    bordered
                  >
                    <template #bodyCell="{ column, record }">
                      <template v-if="column.key === 'action'">
                        <a @click="notify('预约功能（示例）')">{{ record.link }}</a>
                      </template>
                    </template>
                  </a-table>
                </a-col>
                <a-col :md="10" :sm="24" :xs="24">
                  <div class="reminder-card">
                    <h4 class="reminder-title">出诊提醒/候补</h4>
                    <p class="reminder-desc">关注名医出诊变动，开启提醒（演示）。</p>
                    <div class="reminder-actions">
                      <a-button type="primary" @click="notify('已订阅出诊提醒（示例）')">
                        订阅提醒
                      </a-button>
                      <a-button @click="notify('已进入候补名单（示例）')">
                        加入候补
                      </a-button>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- AI共创助手 -->
          <a-col :md="24" :sm="24" :xs="24">
            <a-card class="ai-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                AI共创助手（学派语气与配伍风格）
              </h2>
              <a-row :gutter="[24, 24]">
                <a-col :md="14" :sm="24" :xs="24">
                  <p class="ai-desc">输入专病/证型，生成符合本学派思路的加减建议（演示数据）。</p>
                  <div class="ai-inputs">
                    <a-input
                      v-model:value="aiDisease"
                      placeholder="专病（例：湿疹/痤疮/乳腺结节）"
                    />
                    <a-input
                      v-model:value="aiPattern"
                      placeholder="证型（例：肝郁化火/脾虚湿困）"
                    />
                    <a-button type="primary" @click="generateAiSuggestion">
                      生成建议
                    </a-button>
                  </div>
                  <pre v-if="aiOutput" class="ai-output">{{ aiOutput }}</pre>
                </a-col>
                <a-col :md="10" :sm="24" :xs="24">
                  <div class="ai-link-card">
                    <h4 class="ai-link-title">与"专病—专药"联动</h4>
                    <p class="ai-link-desc">一键推送到"专病"进行证型辨析，或到"专药"生成处方卡与溯源调配。</p>
                    <div class="ai-link-actions">
                      <a-button @click="notify('已推送到专病页面（演示）')">
                        推送到专病
                      </a-button>
                      <a-button @click="notify('已推送到专药页面（演示）')">
                        推送到专药
                      </a-button>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 临床案例 -->
          <a-col :md="24" :sm="24" :xs="24">
            <a-card class="cases-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                临床案例（结构化随访）
              </h2>
              <a-row :gutter="[16, 16]">
                <a-col
                  v-for="caseItem in casesData"
                  :key="caseItem.id"
                  :md="12"
                  :sm="24"
                  :xs="24"
                >
                  <div class="case-card">
                    <h4 class="case-title">病例 {{ caseItem.id }}</h4>
                    <p class="case-detail"><strong>辨证：</strong>{{ caseItem.dx }}</p>
                    <p class="case-detail"><strong>处方：</strong>{{ caseItem.plan }}</p>
                    <p class="case-detail"><strong>随访：</strong>{{ caseItem.follow }}</p>
                    <div class="case-actions">
                      <a-button @click="notify('导出病例（示例）')">
                        导出病例
                      </a-button>
                      <a-button @click="notify('推送到专病页面（示例）')">
                        推送到专病
                      </a-button>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>
        </a-row>
      </a-flex>
    </div>
  </BackgroundImage>
</template>

<script>
import BackgroundImage from "@/components/BackgroundImage.vue";
import NavBar from "@/components/NavBar.vue";
import { SearchOutlined } from "@ant-design/icons-vue";

export default {
  name: "MingyiPage",
  components: {
    BackgroundImage,
    NavBar,
    SearchOutlined,
  },
  data() {
    return {
      searchKeyword: "",
      filterTags: ["内科", "妇科", "儿科", "肝郁", "痰湿", "温病"],
      selectedTags: [],
      aiDisease: "",
      aiPattern: "",
      aiOutput: "",
      lineageData: [
        { year: "1950s", text: "启蒙于江南诸名医，兼收温病与经方思想" },
        { year: "1980s", text: "提出「肝郁脾虚并治」临床体系与关键配伍法" },
        { year: "2000s", text: "成立传承工作室，整理经验方与病例库" },
        { year: "2020s", text: "与数慧岐黄共建数字化学派知识图谱与AI助手" },
      ],
      formulasData: [
        {
          name: "肝郁化火方",
          usage: "肝郁气滞、烦躁易怒、月经不调",
          actions: "疏肝解郁、清热泻火",
          herbs: "柴胡、香附、白芍、栀子、丹皮…",
        },
        {
          name: "健脾除湿方",
          usage: "脾虚湿困、食少困倦、舌苔厚腻",
          actions: "健脾益气、化湿醒脾",
          herbs: "党参、白术、茯苓、陈皮、砂仁…",
        },
        {
          name: "清热解毒方",
          usage: "痤疮湿疹、痒痛糜烂、口苦咽干",
          actions: "清热解毒、利湿止痒",
          herbs: "黄连、黄芩、金银花、连翘、土茯苓…",
        },
      ],
      videosData: [
        { title: "学派核心：肝脾同治的证治思路", length: "18:36", type: "讲座" },
        { title: "配伍哲学：君臣佐使在现代临床的运用", length: "22:14", type: "课程" },
        { title: "专病专方：湿疹与痤疮的辨治", length: "16:05", type: "病例解析" },
      ],
      scheduleColumns: [
        { title: "日期", dataIndex: "date", key: "date" },
        { title: "时段", dataIndex: "time", key: "time" },
        { title: "地点", dataIndex: "place", key: "place" },
        { title: "预约", key: "action" },
      ],
      scheduleData: [
        { date: "周二", time: "上午", place: "岐黄国医馆·一诊室", link: "预约" },
        { date: "周四", time: "下午", place: "岐黄国医馆·特需门诊", link: "预约" },
        { date: "周六", time: "全天", place: "线上视频门诊", link: "预约" },
      ],
      casesData: [
        {
          id: "C-101",
          dx: "湿疹（湿热蕴结）",
          plan: "清热解毒方 外洗配合",
          follow: "1周瘙痒↓，4周皮损基本消退",
        },
        {
          id: "C-205",
          dx: "乳腺结节（肝郁痰凝）",
          plan: "疏肝理气化痰 加减",
          follow: "8周复查结节缩小，症状改善",
        },
      ],
    };
  },
  methods: {
    handleSearch() {
      const keyword = this.searchKeyword.trim();
      if (!keyword) {
        this.$message.warning("请输入关键词");
        return;
      }
      this.$message.success(`搜索"${keyword}"的结果（演示）`);
    },
    toggleTag(tag) {
      const index = this.selectedTags.indexOf(tag);
      if (index > -1) {
        this.selectedTags.splice(index, 1);
      } else {
        this.selectedTags.push(tag);
      }
      const selected = this.selectedTags.length ? this.selectedTags.join("、") : "全部";
      this.$message.info(`筛选：${selected}`);
    },
    generateAiSuggestion() {
      const disease = this.aiDisease.trim() || "湿疹";
      const pattern = this.aiPattern.trim() || "肝郁化火";
      
      this.aiOutput = `【专病】${disease} · 【证型】${pattern}
学派建议：以疏肝理气为纲，合健脾化湿。基础方：肝郁化火方。
加减：
- 瘙痒明显 → 加 白鲜皮、苦参
- 便秘口苦 → 加 大黄（小剂量）、栀子
- 脾虚食少 → 加 党参、山药
提示：情志调摄 + 睡眠管理，外用清热止痒洗剂。`;
    },
    notify(message) {
      this.$message.info(message);
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  padding: 80px 20px 40px;
  min-height: 100vh;
}

.search-card,
.profile-card,
.lineage-card,
.formulas-card,
.videos-card,
.clinic-card,
.ai-card,
.cases-card {
  border-radius: 16px;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(16, 65, 181, 0.12);
  
  :deep(.ant-card-body) {
    padding: 32px;
    
    @media (max-width: 768px) {
      padding: 20px;
    }
  }
}

// 搜索栏
.search-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-chip {
  cursor: pointer;
  padding: 6px 14px;
  border-radius: 999px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(94, 123, 243, 0.2);
  }
}

// 章节标题
.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 22px;
  font-weight: 700;
  color: #1041b5;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    font-size: 19px;
  }
}

.title-bar {
  width: 8px;
  height: 24px;
  background: linear-gradient(180deg, #C9A227 0%, #EED36B 100%);
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(201, 162, 39, 0.3);
}

// 名医档案
.doctor-profile {
  .profile-header {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: center;
      text-align: center;
    }
  }
  
  .doctor-avatar {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(94, 123, 243, 0.15), rgba(16, 65, 181, 0.25));
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 42px;
    font-weight: 700;
    color: #1041b5;
    border: 3px solid rgba(94, 123, 243, 0.2);
    flex-shrink: 0;
  }
  
  .doctor-info {
    flex: 1;
  }
  
  .doctor-name {
    font-size: 24px;
    font-weight: 700;
    color: #1041b5;
    margin-bottom: 8px;
    
    @media (max-width: 768px) {
      font-size: 20px;
    }
  }
  
  .doctor-specialty {
    font-size: 15px;
    color: #666;
    margin-bottom: 12px;
  }
  
  .doctor-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    
    @media (max-width: 768px) {
      justify-content: center;
    }
  }
  
  .academic-summary {
    font-size: 15px;
    line-height: 1.8;
    color: #333;
    margin-bottom: 16px;
    padding: 16px;
    background: linear-gradient(135deg, rgba(94, 123, 243, 0.06), rgba(16, 65, 181, 0.04));
    border-radius: 12px;
    border-left: 4px solid #5e7bf3;
  }
  
  .quick-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    
    .pill {
      display: inline-flex;
      align-items: center;
      padding: 6px 14px;
      border-radius: 999px;
      background: #fff;
      border: 1px dashed #C9A227;
      font-size: 13px;
      color: #1041b5;
    }
  }
}

.contact-card {
  padding: 24px;
  background: linear-gradient(135deg, rgba(94, 123, 243, 0.06), rgba(16, 65, 181, 0.04));
  border-radius: 12px;
  height: 100%;
  
  .contact-title {
    font-size: 18px;
    font-weight: 700;
    color: #1041b5;
    margin-bottom: 16px;
  }
  
  .contact-content {
    display: flex;
    gap: 16px;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: center;
    }
  }
  
  .qr-code {
    width: 104px;
    height: 104px;
    border: 1px dashed rgba(94, 123, 243, 0.4);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: #999;
    flex-shrink: 0;
  }
  
  .contact-info {
    flex: 1;
  }
  
  .contact-desc {
    font-size: 14px;
    color: #666;
    line-height: 1.6;
    margin-bottom: 12px;
  }
  
  .contact-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
}

// 时间线
.timeline {
  position: relative;
  padding-left: 24px;
  
  &::before {
    content: "";
    position: absolute;
    left: 8px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(180deg, #5e7bf3, rgba(94, 123, 243, 0.3));
    border-radius: 2px;
  }
  
  .timeline-item {
    position: relative;
    margin-bottom: 24px;
    
    &::before {
      content: "";
      position: absolute;
      left: -19px;
      top: 6px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: #2D6A4F;
      box-shadow: 0 0 0 4px rgba(45, 106, 79, 0.15);
    }
    
    .timeline-year {
      font-size: 14px;
      font-weight: 700;
      color: #999;
      margin-bottom: 4px;
    }
    
    .timeline-content {
      font-size: 15px;
      color: #333;
      line-height: 1.6;
    }
  }
}

// 验方卡片
.formula-card,
.video-card,
.case-card {
  padding: 20px;
  background: #fff;
  border: 1.5px solid rgba(94, 123, 243, 0.15);
  border-radius: 12px;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(94, 123, 243, 0.2);
    border-color: #5e7bf3;
  }
}

.formula-name,
.video-title,
.case-title {
  font-size: 17px;
  font-weight: 700;
  color: #1041b5;
  margin-bottom: 12px;
}

.formula-detail,
.case-detail {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 8px;
}

.formula-tags,
.formula-actions,
.video-actions,
.case-actions {
  margin-top: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 12px;
}

.video-card {
  text-align: center;
  
  .video-icon {
    font-size: 36px;
    margin-bottom: 12px;
  }
  
  .video-meta {
    font-size: 13px;
    color: #999;
    margin-bottom: 12px;
  }
}

// 门诊排班
.reminder-card {
  padding: 24px;
  background: linear-gradient(135deg, rgba(94, 123, 243, 0.06), rgba(16, 65, 181, 0.04));
  border-radius: 12px;
  height: 100%;
  
  .reminder-title {
    font-size: 18px;
    font-weight: 700;
    color: #1041b5;
    margin-bottom: 12px;
  }
  
  .reminder-desc {
    font-size: 14px;
    color: #666;
    line-height: 1.6;
    margin-bottom: 16px;
  }
  
  .reminder-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
}

// AI助手
.ai-desc {
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
}

.ai-inputs {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
}

.ai-output {
  padding: 16px;
  background: rgba(94, 123, 243, 0.06);
  border: 1px solid rgba(94, 123, 243, 0.15);
  border-radius: 12px;
  font-size: 13px;
  color: #333;
  line-height: 1.8;
  white-space: pre-wrap;
  margin: 0;
}

.ai-link-card {
  padding: 24px;
  background: linear-gradient(135deg, rgba(94, 123, 243, 0.06), rgba(16, 65, 181, 0.04));
  border-radius: 12px;
  height: 100%;
  
  .ai-link-title {
    font-size: 18px;
    font-weight: 700;
    color: #1041b5;
    margin-bottom: 12px;
  }
  
  .ai-link-desc {
    font-size: 14px;
    color: #666;
    line-height: 1.6;
    margin-bottom: 16px;
  }
  
  .ai-link-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
}

// 表格样式
:deep(.ant-table) {
  font-size: 14px;
  
  .ant-table-thead > tr > th {
    background: rgba(94, 123, 243, 0.08);
    color: #1041b5;
    font-weight: 600;
  }
  
  .ant-table-tbody > tr:hover > td {
    background: rgba(94, 123, 243, 0.04);
  }
}

/* 统一按钮样式，与 home 页面保持一致 */
:deep(.ant-btn) {
  border-radius: 16px !important;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  box-shadow: none;
}
</style>
